import { Container, Graphics, Text, type TextStyleOptions } from "pixi.js";
import { t } from "../i18n/index.js";
import {
  COLORS,
  luminance,
  MATCH,
  TEAM_IDENTITY,
  type MatchState,
  type TeamSide,
} from "@golazo/shared";
import { lerp } from "../core/math.js";
import { replayProgress, restartCountdown, varStage } from "../game/replay.js";

const BASE: TextStyleOptions = {
  fontFamily: "Bebas Neue, Impact, system-ui, sans-serif",
  fontWeight: "800",
  fill: COLORS.HUD_TEXT,
};

/**
 * Alto del marcador de escritorio y su separacion del borde.
 *
 * `computePlayViewport` reserva esta franja para que el cesped nunca quede
 * debajo del HUD, asi que los dos numeros TIENEN que salir de aqui: si se
 * escriben a mano en los dos sitios, el dia que alguien retoque el marcador el
 * balon empieza a esconderse detras y nadie sabe por que.
 *
 * Era 98 + 8. En 16:9 el encaje del campo lo limita el ALTO, o sea que esta
 * franja era lo que mas pantalla costaba: mas que el margen de estadio. Bajarla
 * a 64 es lo que de verdad agranda el campo.
 */
export const SCORE_BUG_HEIGHT = 64;
/** La chapa compacta (movil) y el aire que deja al borde de abajo. */
const COMPACT_BUG_H = 46;
const BUG_PAD = 14;

/**
 * Cuanto ocupa el marcador desde el borde de abajo, aire incluido. Lo que se
 * ponga en la franja inferior (el rotulo de retransmision) va por encima de
 * esto, no a ojo.
 */
export function scoreBugClearance(compact: boolean): number {
  return (compact ? COMPACT_BUG_H : SCORE_BUG_HEIGHT) + BUG_PAD;
}
export const SCORE_BUG_TOP = 8;

export interface Hud {
  container: Container;
  /**
   * `kickoffPending` = el saque de centro es TUYO y aun no lo has jugado.
   * Va como bandera y no como `Possession` entera: al HUD le basta saber si
   * tiene que avisar, y meterle el modelo de posesion seria darle acceso a
   * decidir cosas que no le tocan.
   */
  update(match: MatchState, dt: number, kickoffPending?: boolean): void;
  layout(screenWidth: number, screenHeight: number): void;
  setCompact(compact: boolean, topInset: number): void;
  /**
   * Donde esta el balon EN PANTALLA. Es lo que permite que el marcador se
   * aparte solo, y por tanto lo que permite que el campo ocupe toda la ventana
   * en vez de dejarle una franja negra.
   */
  setBallScreen(x: number, y: number): void;
}

/** Alto de cada barra de cine, en fraccion de la pantalla. Ver `drawLetterbox`. */
const REPLAY_BAR_HEIGHT = 0.1;
/** Opacidad de las lineas de barrido de la repeticion (cada 3 px). */
const REPLAY_SCANLINE_ALPHA = 0.08;

export function createHud(): Hud {
  const container = new Container();
  container.label = "hud";

  const flash = new Graphics();
  flash.visible = false;
  container.addChild(flash);

  /*
   * Barras de cine para la repeticion.
   *
   * Antes ponia "REPLAY" en letras gigantes en mitad de la pantalla, que es
   * justo lo que tapa la jugada que se quiere volver a ver. Las barras dicen
   * lo mismo sin una sola palabra —es el lenguaje de television de toda la
   * vida— y ademas encuadran, no tapan.
   *
   * Van ANTES del marcador en el orden de dibujo: el marcador se queda encima,
   * como en una retransmision de verdad.
   */
  const letterbox = new Graphics();
  letterbox.visible = false;
  container.addChild(letterbox);

  /*
   * Lineas de barrido: la textura de una imagen de television, encima del
   * campo solo durante la repeticion (la gradacion de color va aparte, en el
   * partido). Muy tenues y uniformes: un marco de bordes duros ya se probo y
   * se leia como franjas (ver `render/screenFx.ts`). Se dibujan una vez por
   * tamaño de pantalla; por fotograma solo cambia la opacidad.
   */
  const scanlines = new Graphics();
  scanlines.visible = false;
  container.addChild(scanlines);
  const drawScanlines = (): void => {
    scanlines.clear();
    for (let y = 0; y < screenH; y += 3) scanlines.rect(0, y, screenW, 1);
    scanlines.fill({ color: 0x000000, alpha: 1 });
  };

  /*
   * Testigo de cámara: deja claro que la imagen es una repetición sin escribir
   * encima de la jugada. Vive en la esquina superior derecha, dentro de la
   * barra de cine, y el parpadeo rojo imita el piloto de grabación de una
   * cámara de televisión.
   *
   * Y al lado, la palabra. El icono solo era demasiado sutil: quien no conoce
   * el piloto de una cámara de TV veía un dibujito parpadeando y seguía sin
   * saber que la jugada ya había pasado. La palabra no admite interpretación.
   *
   * Sigue sin ser invasiva porque no está en el centro ni sobre el césped: vive
   * DENTRO de la barra de cine, que es espacio que ya estaba negro. Es un
   * rótulo de esquina, no un cartel.
   *
   * "REPLAY" sin `t()`: se dice igual en las dos retransmisiones, y traducirlo a
   * "REPETICIÓN" solo lo haría el doble de largo en el sitio donde menos sitio
   * hay.
   */
  const replayCamera = new Container();
  replayCamera.label = "replay-camera";
  replayCamera.visible = false;
  container.addChild(replayCamera);

  const replayWord = new Text({
    text: "REPLAY",
    style: {
      fontFamily: "DM Sans, system-ui, sans-serif",
      fontSize: 11,
      fontWeight: "700",
      fill: 0xf2fff5,
      letterSpacing: 2.4,
    },
  });
  replayWord.anchor.set(0, 0.5);
  replayWord.x = 49;
  replayWord.y = 14;

  /** Ancho de la chapa entera: icono + palabra. Lo usa la colocación. */
  const REPLAY_PILL_W = 49 + Math.ceil(replayWord.width) + 11;

  const cameraPlate = new Graphics();
  cameraPlate.roundRect(0, 0, REPLAY_PILL_W, 28, 8).fill({ color: 0x050806, alpha: 0.76 });
  cameraPlate.roundRect(7, 7, 23, 15, 4).fill({ color: 0xf2fff5, alpha: 0.94 });
  cameraPlate
    .poly([30, 11, 39, 7, 39, 22, 30, 18])
    .fill({ color: 0xf2fff5, alpha: 0.94 });
  replayCamera.addChild(cameraPlate);
  replayCamera.addChild(replayWord);

  const cameraTally = new Graphics();
  cameraTally.circle(10, 7, 3.2).fill({ color: 0xff3d45 });
  replayCamera.addChild(cameraTally);

  /*
   * Cuenta atrás del saque: 3 · 2 · 1.
   *
   * Es lo único del HUD que SÍ se planta en el centro, y con razón: en ese
   * tramo no hay jugada que tapar —todo el mundo está quieto en su sitio— y lo
   * que hay que comunicar es exactamente cuándo se vuelve a jugar. Un número
   * pequeño en una esquina no serviría: nadie mira las esquinas justo antes de
   * un saque.
   */
  const countdown = new Text({
    text: "",
    style: {
      ...BASE,
      fontSize: 116,
      letterSpacing: 2,
      fill: 0xf2fff5,
      stroke: { color: 0x0d160f, width: 10 },
    },
  });
  countdown.anchor.set(0.5, 0.5);
  countdown.visible = false;
  container.addChild(countdown);

  /*
   * El aviso del saque: "PASA ATRÁS".
   *
   * La regla —no cruzar el medio campo y jugar el balón hacia atrás— es
   * invisible, y una regla invisible que te bloquea el mando no se lee como una
   * regla: se lee como que el juego se ha roto. Con el cartel, el primer saque
   * enseña la norma y no hace falta volver a explicarla.
   *
   * Sale durante la cuenta atrás (cuando el saque es tuyo) y sigue mientras no
   * lo hayas jugado. Debajo del número, pequeño y sin fondo: es una etiqueta,
   * no un diálogo.
   */
  const kickoffHint = new Text({
    text: t("PASA ATRÁS"),
    style: {
      fontFamily: "DM Sans, system-ui, sans-serif",
      fontSize: 13,
      fontWeight: "700",
      fill: 0xffe082,
      letterSpacing: 3,
      stroke: { color: 0x0d160f, width: 4 },
    },
  });
  kickoffHint.anchor.set(0.5, 0);
  kickoffHint.visible = false;
  container.addChild(kickoffHint);

  const scoreBug = new Container();
  scoreBug.label = "score-bug";
  container.addChild(scoreBug);

  const bugBg = new Graphics();
  scoreBug.addChild(bugBg);

  const liveBadge = new Text({
    text: t("EN DIRECTO"),
    style: {
      fontFamily: "DM Sans, system-ui, sans-serif",
      fontSize: 10,
      fontWeight: "700",
      fill: 0xffe082,
      letterSpacing: 2.5,
    },
  });
  liveBadge.anchor.set(0.5, 0);
  liveBadge.y = 6;
  scoreBug.addChild(liveBadge);

  /*
   * Los dos numeros van SEPARADOS y cada uno con el color de su equipo.
   *
   * Antes era un solo texto ("2  -  1") en blanco, y con equipaciones a la
   * carta eso obligaba a recordar de memoria si tu eras el de la izquierda.
   * Pintar cada cifra del color de su camiseta lo resuelve sin escribir un solo
   * nombre — que es lo unico que cabe en una chapa de 46 px de alto.
   */
  const scoreRow = new Container();
  scoreBug.addChild(scoreRow);

  const homeNum = new Text({ text: "0", style: { ...BASE, fontSize: 34 } });
  homeNum.anchor.set(1, 0.5);
  const scoreSep = new Text({
    text: "·",
    style: { ...BASE, fontSize: 30, fill: 0x6f8a74 },
  });
  scoreSep.anchor.set(0.5, 0.5);
  const awayNum = new Text({ text: "0", style: { ...BASE, fontSize: 34 } });
  awayNum.anchor.set(0, 0.5);
  scoreRow.addChild(homeNum, scoreSep, awayNum);

  /*
   * El reloj, ademas de numero, es una BARRA que se vacia.
   *
   * Es la parte que hace que la chapa se lea de reojo: mientras apuntas no
   * puedes pararte a leer "01:12", pero si ves de un vistazo cuanta barra
   * queda. Se vacia desde los dos extremos hacia el centro, asi que los ultimos
   * segundos son un punto que se apaga en mitad del marcador — donde ya estas
   * mirando.
   */
  const timeBar = new Graphics();
  scoreBug.addChild(timeBar);

  const timer = new Text({
    text: "02:00",
    style: {
      fontFamily: "DM Sans, system-ui, sans-serif",
      fontSize: 20,
      fontWeight: "600",
      fill: 0xa8c4ac,
      letterSpacing: 3,
    },
  });
  timer.anchor.set(0.5, 0);
  timer.y = 68;
  scoreBug.addChild(timer);

  const goalOverlay = new Container();
  goalOverlay.visible = false;
  container.addChild(goalOverlay);

  const bannerGlow = new Text({
    text: "",
    style: {
      ...BASE,
      fontSize: 72,
      letterSpacing: 8,
      fill: 0xffffff,
      stroke: { color: 0xffffff, width: 10 },
    },
  });
  bannerGlow.anchor.set(0.5, 0.5);
  bannerGlow.alpha = 0.35;
  goalOverlay.addChild(bannerGlow);

  const banner = new Text({
    text: "",
    style: {
      ...BASE,
      fontSize: 72,
      letterSpacing: 8,
      stroke: { color: 0x0d160f, width: 8 },
    },
  });
  banner.anchor.set(0.5, 0.5);
  goalOverlay.addChild(banner);

  const goalSub = new Text({
    text: "",
    style: {
      fontFamily: "DM Sans, system-ui, sans-serif",
      fontSize: 18,
      fontWeight: "700",
      fill: 0xf2fff5,
      letterSpacing: 4,
      stroke: { color: 0x0d160f, width: 4 },
    },
  });
  goalSub.anchor.set(0.5, 0);
  goalSub.y = 52;
  goalOverlay.addChild(goalSub);

  let screenW = 800;
  let screenH = 600;
  let goalAnim = 0;
  let flashAlpha = 0;
  let prevPhase: MatchState["phase"] = "playing";
  let prevHome = 0;
  let prevAway = 0;
  let scorePop = 0;
  /** Balon en pantalla, para que la chapa se quite de en medio. */
  let ballScreenX = -9999;
  let ballScreenY = -9999;
  /** Opacidad suavizada de la chapa: sin suavizar parpadea al pasar el balon. */
  let bugFade = 1;
  let clockPulse = 0;
  let homePulse = 0;
  let awayPulse = 0;
  let bannerShine = 0;
  let compactMode = false;
  /** 0 = sin barras, 1 = barras dentro. Entra rapido y sale mas rapido. */
  let boxT = 0;
  let replayCameraPulse = 0;

  /** Alto de la chapa del marcador en modo compacto. Lo usa la colocacion. */
  /**
   * A que distancia de la chapa empieza a estorbar el balon.
   *
   * 46 px es algo mas que el radio del jugador dibujado: la chapa se aparta
   * cuando el balon todavia no la ha tocado, no cuando ya esta debajo.
   */
  const BUG_CLEAR_GAP = 46;
  /** Lo que ocupan los botones de ventana/camara en la esquina superior derecha. */
  const CHROME_CORNER_W = 124;

  /** Ancho de la chapa compacta: el mismo que dibuja `drawBugBg`. */
  const compactBugWidth = (): number => Math.min(300, screenW - 24);

  /**
   * Donde va el marcador: ABAJO Y AL CENTRO.
   *
   * Vivia abajo a la izquierda, y en movil y tablet eso lo dejaba justo debajo
   * del pulgar que dirige al jugador: para ver el resultado habia que levantar
   * la mano en mitad de una jugada. El centro es el unico sitio de la franja
   * inferior que ningun pulgar tapa, porque los dos controles tactiles viven en
   * las esquinas.
   *
   * Arriba tampoco valia: ahi esta el minimapa, y encima es donde miras cuando
   * atacas hacia el fondo.
   *
   * `goalOverlay` y las barras compensan el desplazamiento para seguir en
   * coordenadas de pantalla: son pantalla completa, no marcador.
   */
  const placeBug = (): void => {
    const height = compactMode ? COMPACT_BUG_H : SCORE_BUG_HEIGHT;
    container.x = screenW / 2;
    container.y = screenH - height - BUG_PAD;
    goalOverlay.x = screenW / 2 - container.x;
    goalOverlay.y = screenH / 2 - 24 - container.y;
    countdown.x = screenW / 2 - container.x;
    countdown.y = screenH / 2 - 24 - container.y;
    // Justo debajo del numero, y a la misma altura cuando el numero no esta.
    kickoffHint.x = countdown.x;
    kickoffHint.y = countdown.y + 44;
    letterbox.x = -container.x;
    letterbox.y = -container.y;
    scanlines.x = -container.x;
    scanlines.y = -container.y;
    // 12 px de aire al borde. El ancho sale de la chapa, no de un número a
    // mano: al añadir la palabra, un 55 escrito aquí la habría sacado de
    // pantalla por la derecha.
    // Deja sitio a los botones de ventana y camara, que ahora viven en esa
    // misma esquina y son DOM (van por encima del canvas pase lo que pase).
    replayCamera.x = screenW - container.x - REPLAY_PILL_W - 12 - CHROME_CORNER_W;
    replayCamera.y = -container.y + 5;
    flash.x = -container.x;
    flash.y = -container.y;
  };

  /**
   * Barras arriba y abajo. La altura sale de la pantalla, no de un numero
   * fijo. Era un 5,5 %, y los jugadores pedian "barras mas grandes" (Discord,
   * 2026-09-28): a esa altura se leian como un marco, no como un plano de
   * repeticion. Al 10 % es el formato de cine de verdad, y en un movil en
   * horizontal (360 px) la chapa de REPLAY por fin cabe DENTRO de la barra.
   *
   * No son negro puro (alpha 0,9): un corte totalmente opaco se siente como
   * una pantalla de carga, y esto tiene que sentirse como una camara.
   */
  const drawLetterbox = (): void => {
    letterbox.visible = boxT > 0.001;
    scanlines.visible = letterbox.visible;
    if (!letterbox.visible) {
      letterbox.clear();
      return;
    }
    // Suavizado en la entrada y en la salida, para que las barras se deslicen.
    const eased = boxT < 0.5 ? 2 * boxT * boxT : 1 - (1 - boxT) ** 2 * 2;
    scanlines.alpha = eased * REPLAY_SCANLINE_ALPHA;
    const h = screenH * REPLAY_BAR_HEIGHT * eased;
    letterbox.clear();
    letterbox.rect(0, 0, screenW, h).fill({ color: 0x000000, alpha: 0.9 });
    letterbox.rect(0, screenH - h, screenW, h).fill({ color: 0x000000, alpha: 0.9 });
  };

  /**
   * El color de un equipo, pero legible SOBRE LA CHAPA.
   *
   * La chapa es casi negra, asi que una equipacion oscura (granate, azul
   * marino, negra...) pintaba un numero que no se veia. Y como las equipaciones
   * son a medida, esto no se puede resolver eligiendo bien un color: hay que
   * calcularlo. Por debajo del umbral se mezcla con blanco hasta que levanta,
   * conservando el tono — sigue reconociendose como "el color de los tuyos".
   */
  const readable = (color: number): number => {
    const lum = luminance(color);
    if (lum >= 0.42) return color;
    const t = Math.min(0.72, (0.42 - lum) * 1.6);
    const mix = (shift: number): number => {
      const c = (color >> shift) & 0xff;
      return Math.round(c + (255 - c) * t) & 0xff;
    };
    return (mix(16) << 16) | (mix(8) << 8) | mix(0);
  };

  /** Ancho de la chapa segun el modo. Lo usan el fondo y la barra de tiempo. */
  const bugWidth = (): number => (compactMode ? compactBugWidth() : 300);
  const bugHeight = (): number => (compactMode ? COMPACT_BUG_H : SCORE_BUG_HEIGHT);

  /*
   * La chapa: una capsula centrada, sin bordes duros.
   *
   * El color de cada equipo ya no es una franja pegada al canto —invisible
   * cuando la chapa esta al 55% de opacidad— sino que vive en la BARRA DE
   * TIEMPO, que es lo unico que se mueve. Asi el adorno y la informacion son la
   * misma cosa, que es lo que hace que quepa en 46 px sin parecer un tablero.
   */
  const drawBugBg = (): void => {
    const w = bugWidth();
    const h = bugHeight();
    const r = h / 2;
    bugBg.clear();
    bugBg.roundRect(-w / 2, 0, w, h, r).fill({ color: 0x08120c, alpha: 0.72 });
    bugBg
      .roundRect(-w / 2, 0, w, h, r)
      .stroke({ width: 1, color: COLORS.HUD_TEXT, alpha: 0.14 });
  };

  /**
   * La barra de tiempo: se vacia desde los extremos hacia el centro.
   *
   * `left` es el tramo del local y `right` el del visitante, cada uno con su
   * color, asi que la barra dice dos cosas a la vez: cuanto queda y de quien es
   * cada lado del marcador. Cuando el partido esta a punto de acabar solo queda
   * un punto encendido en el centro.
   */
  const drawTimeBar = (remaining01: number): void => {
    const w = bugWidth() - 26;
    const h = bugHeight();
    const y = h - 7;
    const half = w / 2;
    const live = Math.max(0, Math.min(1, remaining01)) * half;
    timeBar.clear();
    // El raíl completo, muy tenue: sin el, una barra casi vacia parece rota.
    timeBar.roundRect(-half, y, w, 3, 1.5).fill({ color: COLORS.HUD_TEXT, alpha: 0.12 });
    if (live <= 0.5) return;
    timeBar
      .roundRect(-live, y, live, 3, 1.5)
      .fill({ color: readable(TEAM_IDENTITY.home.kit.primary), alpha: 0.92 });
    timeBar
      .roundRect(0, y, live, 3, 1.5)
      .fill({ color: readable(TEAM_IDENTITY.away.kit.primary), alpha: 0.92 });
  };

  const applyCompactStyles = (): void => {
    // La cuenta atras encoge en tactil por la misma razon que el resto del HUD:
    // 116 px de numero en un movil en horizontal ocupan medio campo, y aunque
    // ahi no haya jugada que tapar, se lee como un error de escala.
    countdown.style.fontSize = compactMode ? 76 : 116;

    /*
     * Una sola fila: numero - punto - numero al centro, el reloj a la derecha y
     * el rotulo "EN DIRECTO" a la izquierda. Apilar en pisos era lo que obligaba
     * a una chapa alta, y el alto es lo que se le quita al campo.
     */
    const h = bugHeight();
    const mid = h / 2 - 3;
    const half = bugWidth() / 2;

    if (compactMode) {
      liveBadge.style.fontSize = 7;
      liveBadge.style.letterSpacing = 1.6;
      liveBadge.anchor.set(0, 0.5);
      liveBadge.x = -half + 13;
      liveBadge.y = mid;
      homeNum.style.fontSize = 26;
      awayNum.style.fontSize = 26;
      scoreSep.style.fontSize = 22;
      homeNum.x = -11;
      awayNum.x = 11;
      scoreRow.y = mid;
      timer.style.fontSize = 14;
      timer.style.letterSpacing = 1.6;
      timer.anchor.set(1, 0.5);
      timer.x = half - 13;
      timer.y = mid;
      return;
    }
    liveBadge.style.fontSize = 9;
    liveBadge.style.letterSpacing = 2.2;
    liveBadge.anchor.set(0, 0.5);
    liveBadge.x = -half + 16;
    liveBadge.y = mid;
    homeNum.style.fontSize = 34;
    awayNum.style.fontSize = 34;
    scoreSep.style.fontSize = 28;
    homeNum.x = -14;
    awayNum.x = 14;
    scoreRow.y = mid;
    timer.style.fontSize = 17;
    timer.style.letterSpacing = 2.2;
    timer.anchor.set(1, 0.5);
    timer.x = half - 16;
    timer.y = mid;
  };
  drawBugBg();
  applyCompactStyles();

  /**
   * Como se llama el equipo en el rotulo.
   *
   * Sale de la identidad viva, asi que si has puesto nombre a los tuyos el
   * "GOL DEL VISITANTE" pasa a ser el nombre que elegiste. Es la mitad de la
   * fantasia: los colores la ves, el nombre lo lees.
   */
  const teamLabel = (side: TeamSide | null | undefined): string =>
    side === "away" ? TEAM_IDENTITY.away.name : TEAM_IDENTITY.home.name;

  return {
    container,

    update(match: MatchState, dt: number, kickoffPending = false): void {
      if (match.score.home !== prevHome) homePulse = 1;
      if (match.score.away !== prevAway) awayPulse = 1;
      if (match.score.home !== prevHome || match.score.away !== prevAway) {
        scorePop = 1;
      }
      prevHome = match.score.home;
      prevAway = match.score.away;

      const replay = replayProgress(match);
      const replaying = replay > 0;
      /*
       * La cuenta atras es el ultimo tramo de la pausa por gol, y manda sobre
       * todo lo demas del cartel: mientras corre, ni banner ni repeticion.
       */
      const counting = restartCountdown(match);
      const countingDown = counting > 0;

      homeNum.text = String(match.score.home);
      awayNum.text = String(match.score.away);
      homeNum.style.fill = readable(TEAM_IDENTITY.home.kit.primary);
      awayNum.style.fill = readable(TEAM_IDENTITY.away.kit.primary);
      /*
       * En gol de oro el reloj esta parado en 0 pero se sigue jugando. Un
       * `00:00` fijo se lee como "esto ya ha terminado" justo en el momento
       * mas tenso del partido, asi que el reloj cede su sitio al aviso.
       */
      timer.text = match.goldenGoal ? t("GOL DE ORO") : formatClock(match.timeRemaining);
      /*
       * Con varios tiempos hay que decir en cual vas: si no, el reloj llega a
       * cero, sigue el partido y no hay forma de saber si esto es el descanso,
       * el gol de oro o un fallo.
       */
      liveBadge.text = match.goldenGoal
        ? t("MUERTE SÚBITA")
        : match.breakPause > 0
          ? t("DESCANSO")
          : match.periods > 1
            ? `${match.period}/${match.periods}`
            : t("EN DIRECTO");

      scorePop = Math.max(0, scorePop - dt * 5);
      homePulse = Math.max(0, homePulse - dt * 4);
      awayPulse = Math.max(0, awayPulse - dt * 4);

      // El que marca da el salto, no los dos: asi el propio marcador dice de
      // quien fue sin ningun rotulo.
      homeNum.scale.set(1 + homePulse * 0.35);
      awayNum.scale.set(1 + awayPulse * 0.35);
      scoreSep.alpha = 0.55 + scorePop * 0.45;

      // La barra mide el TIEMPO en curso, no el partido entero: con dos tiempos
      // una barra del total se quedaria a la mitad en el descanso y parece que
      // se ha colgado.
      drawTimeBar(match.timeRemaining / MATCH.DURATION);


      const urgent =
        match.goldenGoal || (match.phase === "playing" && match.timeRemaining <= 30);
      if (urgent) {
        clockPulse += dt * 6;
        const pulse = 1 + Math.sin(clockPulse) * 0.06;
        timer.scale.set(pulse);
        const critical = match.goldenGoal || match.timeRemaining <= 10;
        timer.style.fill = critical ? 0xff8a8e : 0xffd76e;
        liveBadge.style.fill = critical ? 0xff8a8e : 0xffe082;
      } else {
        timer.scale.set(1);
        timer.style.fill = 0xa8c4ac;
        liveBadge.style.fill = 0xffe082;
        clockPulse = 0;
      }

      /*
       * La chapa se quita de en medio.
       *
       * Vive encima del cesped (el campo ya ocupa la ventana entera), asi que
       * lo que antes resolvia una franja reservada lo resuelve ahora esto: en
       * reposo se queda al 62% y, si el balon entra en su zona, baja al 15% —
       * translucida, no invisible, para que no parezca un fallo. Vuelve a plena
       * opacidad sola cuando alguien marca o cuando el reloj aprieta, que son
       * los dos momentos en que de verdad se mira el resultado.
       */
      const w = bugWidth();
      const h = bugHeight();
      const nearX = Math.abs(ballScreenX - container.x) - w / 2;
      const nearY = Math.abs(ballScreenY - (container.y + h / 2)) - h / 2;
      const gap = Math.max(nearX, nearY);
      const intruding = gap < BUG_CLEAR_GAP;
      const wanted = intruding ? 0.15 : 0.62;
      // Sube rapido (hay que poder leerlo ya) y baja despacio (que no parpadee).
      const speed = wanted > bugFade ? 8 : 3;
      bugFade += (wanted - bugFade) * Math.min(1, dt * speed);
      const insist = Math.max(scorePop, urgent ? 1 : 0, countingDown ? 1 : 0);
      scoreBug.alpha = Math.min(1, bugFade + insist * (1 - bugFade));

      if (match.phase === "goal" && prevPhase !== "goal") {
        goalAnim = 0;
        flashAlpha = 0.6;
        bannerShine = 1;
      }
      if (match.phase === "offside" && prevPhase !== "offside") {
        goalAnim = 0;
        flashAlpha = 0.35;
      }
      prevPhase = match.phase;

      /*
       * Durante la cuenta el cartel se APAGA, no se esconde.
       *
       * Esconderlo sin bajar `goalAnim` dejaba el ¡GOLAZO! cargado a 1: al
       * volver a jugar, la rama de `playing` lo encontraba todavia por encima
       * de 0,01 y lo enseñaba otra vez, tres segundos despues del gol y encima
       * del saque. Se apaga aqui para que no quede nada que reaparecer.
       */
      if (match.phase === "goal" && !countingDown) {
        goalAnim = Math.min(1, goalAnim + dt / 0.35);
        flashAlpha = Math.max(0, flashAlpha - dt * 1.6);
        bannerShine = Math.max(0, bannerShine - dt * 2.2);
      } else if (match.phase === "offside") {
        goalAnim = Math.min(1, goalAnim + dt / 0.28);
        flashAlpha = Math.max(0, flashAlpha - dt * 2.2);
      } else {
        goalAnim = Math.max(0, goalAnim - dt * 3);
        flashAlpha = Math.max(0, flashAlpha - dt * 4);
        bannerShine = 0;
      }

      const showOverlay =
        (match.phase === "goal" && !replaying && !countingDown) ||
        match.phase === "offside" ||
        (match.phase === "playing" && goalAnim > 0.01);

      goalOverlay.visible = showOverlay;

      /*
       * El aviso vive mas que la cuenta: aparece con ella y no se va hasta que
       * el saque esta jugado. Si desapareciera con el "1", el jugador se
       * quedaria con el balon, el mando bloqueado y ninguna explicacion.
       */
      kickoffHint.visible = kickoffPending;

      countdown.visible = countingDown;
      if (countingDown) {
        // Tope en 3: la cola de `RESTART_COUNTDOWN` (los 0,3 s que dejan salir
        // las barras de cine) caeria en un "4" que nadie ha pedido.
        countdown.text = String(Math.min(3, Math.ceil(counting)));
        /*
         * Cada numero entra grande y se asienta. El pulso no es adorno: sin el,
         * el 3 y el 2 se leen como un solo numero que cambia de forma, y la
         * cuenta deja de marcar el tiempo, que es su unico trabajo.
         */
        const beat = Math.min(1, (1 - (counting % 1)) * 3.2);
        countdown.scale.set(lerp(1.5, 1, 1 - (1 - beat) ** 3));
        countdown.alpha = Math.min(1, 0.25 + beat * 2);
      }

      /*
       * Las barras entran en 0,28 s y salen en 0,18 s. Entrar despacio se
       * notaria como un tiron a mitad de jugada; salir despacio dejaria el
       * campo recortado cuando ya se esta sacando de centro.
       */
      // El congelado del VAR conserva las barras: sigue siendo material de
      // repeticion, y quitarlas justo ahi devolveria el "EN DIRECTO" sobre una
      // imagen que no esta pasando.
      const cinematic = replaying || varStage(match) === "freeze";
      boxT = cinematic
        ? Math.min(1, boxT + dt / 0.28)
        : Math.max(0, boxT - dt / 0.18);
      drawLetterbox();

      replayCamera.visible = replaying;
      if (replaying) {
        replayCameraPulse += dt;
        const blink = 0.5 + 0.5 * Math.sin(replayCameraPulse * Math.PI * 4);
        replayCamera.alpha = 0.58 + blink * 0.42;
        cameraTally.scale.set(0.82 + blink * 0.28);
      } else {
        replayCameraPulse = 0;
      }

      /*
       * Y el "EN DIRECTO" se apaga: es la senal mas honesta y la mas discreta
       * que hay de que lo que se ve no esta pasando ahora. Se atenua en vez de
       * desaparecer para que no parezca que el marcador se ha roto.
       */
      liveBadge.alpha = 1 - boxT * 0.75;

      if (match.phase === "goal") {
        if (replaying) {
          // Durante el clip no hay cartel: la jugada se ve entera.
        } else {
          banner.text = t("¡GOLAZO!");
          bannerGlow.text = t("¡GOLAZO!");
          const accent = TEAM_IDENTITY[match.lastScorer ?? "home"].kit.accent;
          banner.style.fill = accent;
          goalSub.text = `${t("GOL ·")} ${teamLabel(match.lastScorer)}`;
        }
      } else if (match.phase === "offside") {
        /*
         * El cartel cuenta la revision paso a paso, como una retransmision.
         *
         * Antes ponia "VAR · ANULADO" desde el primer fotograma: soltaba el
         * veredicto antes de enseñar nada, asi que la revision no revisaba, solo
         * hacia esperar. Ahora primero se avisa, luego se enseña la jugada, y el
         * fallo llega con la linea trazada delante.
         */
        const stage = varStage(match);
        if (stage === "replay") {
          // Durante el clip el cartel se aparta: la jugada se ve entera.
          banner.text = "";
          bannerGlow.text = "";
          goalSub.text = t("VAR · REVISANDO");
        } else if (stage === "freeze") {
          banner.text = t("FUERA DE JUEGO");
          bannerGlow.text = t("FUERA DE JUEGO");
          banner.style.fill = 0xffd76e;
          goalSub.text = `${t("POSICIÓN ILEGAL ·")} ${teamLabel(match.offsideTeam)}`;
        } else {
          banner.text = t("EL LINIER LEVANTA");
          bannerGlow.text = t("EL LINIER LEVANTA");
          banner.style.fill = 0xffd76e;
          goalSub.text = t("VAR · REVISANDO");
        }
      } else {
        goalSub.text = "";
      }

      const pop = goalAnim < 1 ? elasticOut(goalAnim) : 1;
      goalOverlay.scale.set(lerp(0.15, 1.08, pop));
      goalOverlay.alpha = Math.min(1, goalAnim * 2.2);
      bannerGlow.alpha = 0.25 + bannerShine * 0.35;
      bannerGlow.scale.set(1 + bannerShine * 0.04);

      flash.visible = flashAlpha > 0.01;
      if (flash.visible) {
        flash.clear();
        flash.rect(0, 0, screenW, screenH).fill({
          color: TEAM_IDENTITY[match.lastScorer ?? "home"].kit.accent,
          alpha: flashAlpha * 0.32,
        });
      }
    },

    layout(screenWidth: number, screenHeight: number): void {
      screenW = screenWidth;
      screenH = screenHeight;
      drawBugBg();
      placeBug();
      drawScanlines();
    },

    setBallScreen(x: number, y: number): void {
      ballScreenX = x;
      ballScreenY = y;
    },

    setCompact(compact: boolean, _nextTopInset: number): void {
      if (compactMode !== compact) {
        compactMode = compact;
        drawBugBg();
        applyCompactStyles();
      }
      placeBug();
    },
  };
}

function formatClock(seconds: number): string {
  const clamped = Math.max(0, Math.ceil(seconds));
  const minutes = Math.floor(clamped / 60);
  const rest = clamped % 60;
  return `${String(minutes).padStart(2, "0")}:${String(rest).padStart(2, "0")}`;
}

function elasticOut(t: number): number {
  if (t <= 0) return 0;
  if (t >= 1) return 1;
  const p = 0.35;
  return Math.pow(2, -10 * t) * Math.sin(((t - p / 4) * (2 * Math.PI)) / p) + 1;
}
